import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { Input, Spinner } from 'reactstrap';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import toast from 'react-hot-toast';
import {
  fetchDriverAnalysisExport,
  fetchDriverAnalysisFilterOptions,
} from '../actions/action';

/* Same country list the Delivery Feedback export offers. */
const COUNTRY_OPTIONS = [
  {
    label: 'Country',
    options: [
      { label: 'All', value: 'all' },
      { label: 'UAE', value: 'ae' },
      { label: 'OMAN', value: 'om' },
      { label: 'BAHRAIN', value: 'bh' },
      { label: 'QATAR', value: 'qa' },
      { label: 'KUWAIT', value: 'kw' },
    ],
  },
];

/* react-select sizes itself off its own styles rather than bootstrap classes,
   so it has to be pinned to the same height as a default-size Input - which is
   what the rest of the page's controls use. */
const CONTROL_HEIGHT = '38px';
const SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: CONTROL_HEIGHT,
    height: CONTROL_HEIGHT,
  }),
  valueContainer: (base) => ({ ...base, height: CONTROL_HEIGHT }),
  indicatorsContainer: (base) => ({ ...base, height: CONTROL_HEIGHT }),
  /* The menu is rendered into document.body (see menuPortalTarget) so it is not
     clipped by the toolbar, and it sits above the rsuite date picker, which
     otherwise draws over the options. */
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const DEFAULT_COUNTRY = 'all';

/* Longest range either date picker allows, counting both end days. */
const MAX_RANGE_DAYS = 31;
const { allowedMaxDays } = DateRangePicker;

const rangeDays = (from, to) => moment(to).diff(moment(from), 'days') + 1;

/**
 * Driver Analysis xlsx export.
 *
 * Mirrors the Delivery Feedback export: posts the country + date range, then
 * redirects the browser to the report_link the API returns.
 */
const DriverAnalysisExport = () => {
  const dispatch = useDispatch();

  /* Both date ranges start blank. At least one of them - delivery date
     (delivery_date) or transaction date (created_at) - must be picked before
     exporting, unless an SO / ERP number is given, which exports that one
     order regardless of dates. Picking both narrows the report to rows that
     match both. */
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [deliveryStartDate, setDeliveryStartDate] = useState('');
  const [deliveryEndDate, setDeliveryEndDate] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [dispatchLocation, setDispatchLocation] = useState('');
  const [dispatchLocations, setDispatchLocations] = useState([]);
  const [rating, setRating] = useState('');
  const [reference, setReference] = useState('');
  const [isCountryError, setCountryError] = useState(false);
  const [isExporting, setExporting] = useState(false);

  /* Dispatch locations are country specific, so they reload whenever the
     country changes and any stale selection is dropped. Same source as the
     listing dropdown - distinct values off the captured rows. */
  useEffect(() => {
    let isCurrent = true;

    const loadDispatchLocations = async () => {
      const response = await fetchDriverAnalysisFilterOptions({
        params: { country: selectedCountry || 'all' },
        dispatch,
      });
      if (isCurrent) {
        setDispatchLocations(response?.dispatchLocations || []);
      }
    };

    setDispatchLocation('');
    loadDispatchLocations();

    return () => {
      isCurrent = false;
    };
  }, [selectedCountry]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!selectedCountry) {
      setCountryError(true);
      return;
    }
    setCountryError(false);

    const hasDeliveryRange = Boolean(deliveryStartDate && deliveryEndDate);
    const hasTransactionRange = Boolean(startDate && endDate);
    if (!reference.trim()) {
      if (!hasDeliveryRange && !hasTransactionRange) {
        setDateError('Select a date range.');
        return;
      }
      if (
        (hasDeliveryRange &&
          rangeDays(deliveryStartDate, deliveryEndDate) > MAX_RANGE_DAYS) ||
        (hasTransactionRange && rangeDays(startDate, endDate) > MAX_RANGE_DAYS)
      ) {
        setDateError(`Max ${MAX_RANGE_DAYS} days.`);
        return;
      }
    }
    setDateError('');

    setExporting(true);
    const response = await fetchDriverAnalysisExport({
      params: {
        // Delivery date range - only sent when one was picked.
        date_from: deliveryStartDate,
        date_to: deliveryEndDate || deliveryStartDate,
        // Transaction date range.
        created_from: startDate,
        created_to: endDate || startDate,
        country: selectedCountry,
        dispatch_location: dispatchLocation,
        rating,
        // Overrides both date ranges when supplied.
        so_no: reference.trim(),
      },
      dispatch,
    });
    setExporting(false);

    if (response?.status === 'success') {
      toast.success(response?.message || 'Report has been sent to user');
    } else {
      toast.error(response?.message || 'Export failed');
    }
  };

  const handleDateChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setStartDate(moment(dates[0]).format('YYYY-MM-DD'));
      setEndDate(moment(dates[1]).format('YYYY-MM-DD'));
      setDateError('');
    }
  };

  const handleDateClean = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleDeliveryDateChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setDeliveryStartDate(moment(dates[0]).format('YYYY-MM-DD'));
      setDeliveryEndDate(moment(dates[1]).format('YYYY-MM-DD'));
      setDateError('');
    }
  };

  const handleDeliveryDateClean = () => {
    setDeliveryStartDate('');
    setDeliveryEndDate('');
  };

  return (
    <form onSubmit={handleSubmit} className="w-100">
      {/* Right aligned: the bar shares the header row with the page title, so
          once it wraps both lines stay flush with the right edge instead of
          starting under the title. */}
      <div className="d-flex align-items-center flex-wrap flex-xl-nowrap justify-content-end gap-2 w-100">
        <div style={{ flex: '0 1 120px', minWidth: 0, position: 'relative' }}>
          <Select
            value={
              COUNTRY_OPTIONS[0].options.find(
                (option) => option.value === selectedCountry,
              ) || null
            }
            onChange={(option) => setSelectedCountry(option?.value)}
            options={COUNTRY_OPTIONS}
            placeholder="Country"
            classNamePrefix="select2-selection"
            styles={SELECT_STYLES}
            menuPortalTarget={
              typeof document !== 'undefined' ? document.body : null
            }
            menuPosition="fixed"
          />
          {isCountryError && (
            <span
              style={{
                position: 'absolute',
                top: 38,
                left: 0,
                color: '#f00',
                fontSize: '11px',
              }}
            >
              Please Select Country.
            </span>
          )}
        </div>
        <div style={{ flex: '0 1 165px', minWidth: 0 }}>
          <DateRangePicker
            size="md"
            className="w-100"
            placement="auto"
            placeholder="Delivery Date"
            format="dd MMM yy"
            character=" - "
            showOneCalendar
            disabledDate={allowedMaxDays(MAX_RANGE_DAYS)}
            onChange={handleDeliveryDateChange}
            onClean={handleDeliveryDateClean}
            disabled={reference.trim() !== ''}
          />
        </div>
        <div style={{ flex: '0 1 165px', minWidth: 0, position: 'relative' }}>
          <DateRangePicker
            size="md"
            className="w-100"
            placement="auto"
            placeholder="Transaction Date"
            format="dd MMM yy"
            character=" - "
            showOneCalendar
            disabledDate={allowedMaxDays(MAX_RANGE_DAYS)}
            // Controlled, so what is shown is exactly what gets sent.
            value={
              startDate && endDate
                ? [moment(startDate).toDate(), moment(endDate).toDate()]
                : null
            }
            onChange={handleDateChange}
            onClean={handleDateClean}
            disabled={reference.trim() !== ''}
          />
          {dateError && (
            <span
              style={{
                position: 'absolute',
                top: 38,
                left: 0,
                color: '#f00',
                fontSize: '11px',
                whiteSpace: 'nowrap',
              }}
            >
              {dateError}
            </span>
          )}
        </div>
        <div style={{ flex: '0 1 170px', minWidth: 0 }}>
          <Input
            type="select"
            value={dispatchLocation}
            onChange={(e) => setDispatchLocation(e.target.value)}
            title="Limits the report to one dispatch location"
          >
            <option value="">All Dispatch Locations</option>
            {dispatchLocations.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </Input>
        </div>
        <div style={{ flex: '0 1 130px', minWidth: 0 }}>
          <Input
            type="select"
            value={rating}
            onChange={(e) => setRating(e.target.value)}
            title="Limits the report to rated or non-rated deliveries"
          >
            <option value="">All Ratings</option>
            <option value="rated">Rated</option>
            <option value="non_rated">Non Rated</option>
          </Input>
        </div>
        <div style={{ flex: '0 1 135px', minWidth: 0 }}>
          <Input
            type="text"
            placeholder="SO No / ERP No"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            title="Exports this order only, ignoring the date range"
          />
        </div>
        <button
          type="submit"
          className="btn btn-dark d-flex align-items-center flex-shrink-0 text-nowrap"
        >
          {isExporting && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-1"
            />
          )}
          Export
          <i className="bx bx-download" style={{ padding: '3px' }}></i>
        </button>
      </div>
    </form>
  );
};

export default DriverAnalysisExport;
